import { useEffect, useMemo, useRef, useState } from "react";
import {
  Tag,
  Users,
  Building2,
  Send,
  CalendarClock,
  Sliders,
  CheckSquare,
  Square,
  Search,
  Save,
  X,
  AlertCircle,
  Plus,
} from "lucide-react";
import { cn } from "../../../../utils/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/UI/Popover";
import shopApi from "../../../../api/shopApi";
import {
  isZaloTagEvent,
  isAkabizTagEvent,
} from "../../utils/eventType";

const TARGET_OPTIONS = [
  { value: "all", label: "Tất cả" },
  { value: "group", label: "Nhóm" },
  { value: "user", label: "User cá nhân" },
];

const SCHEDULE_OPTIONS = [
  { value: "immediate", label: "Gửi ngay" },
  { value: "delay", label: "Sau khoảng thời gian" },
  { value: "specific_time", label: "Vào giờ cụ thể" },
];

const DELAY_UNITS = [
  { value: "seconds", label: "Giây" },
  { value: "minutes", label: "Phút" },
  { value: "hours", label: "Giờ" },
  { value: "days", label: "Ngày" },
];

const DEFAULTS = {
  description: "",
  eventType: "",
  contentReply: "",
  shopIds: [],
  targetType: "user",
  isActive: true,
  conditionValue: [],
  scheduleType: "immediate",
  delayValue: null,
  delayUnit: "minutes",
  specificTime: null,
  isAddFriend: false,
};

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const areArraysEqual = (left, right) => {
  const a = Array.isArray(left) ? left.map(String) : [];
  const b = Array.isArray(right) ? right.map(String) : [];
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
};

const BotConfigForm = ({
  mode = "edit",
  initial,
  eventTypes,
  shopsList,
  tagsList,
  zaloTagsList,
  chatbotType,
  onSubmit,
  onCancel,
}) => {
  const seed = useMemo(() => {
    if (!initial) {
      return {
        ...DEFAULTS,
        eventType: eventTypes[0]?.action || "",
      };
    }
    return {
      description: initial.description || "",
      eventType: initial.eventType || "",
      contentReply: initial.contentReply || "",
      shopIds: Array.isArray(initial.shopIds) ? [...initial.shopIds] : [],
      targetType: initial.targetType || "user",
      isActive: initial.isActive !== false,
      conditionValue: Array.isArray(initial.conditionValue)
        ? [...initial.conditionValue]
        : [],
      scheduleType: initial.scheduleType || "immediate",
      delayValue: initial.delayValue ?? null,
      delayUnit: initial.delayUnit || "minutes",
      specificTime: initial.specificTime
        ? String(initial.specificTime).slice(0, 5)
        : null,
      isAddFriend: initial.isAddFriend === true,
    };
  }, [initial, eventTypes]);

  const [form, setForm] = useState(seed);
  const [groupsByShop, setGroupsByShop] = useState({});
  const groupsRef = useRef({});
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const eventTypeMetaByAction = useMemo(
    () =>
      new Map(
        (Array.isArray(eventTypes) ? eventTypes : [])
          .filter((item) => item?.action)
          .map((item) => [item.action, item]),
      ),
    [eventTypes],
  );

  const selectedEventMeta = eventTypeMetaByAction.get(form.eventType) || null;
  const eventConditionKind = selectedEventMeta?.conditionKind || null;
  const requiresSingleShop = eventConditionKind != null;
  const supportsAddFriend = selectedEventMeta?.supportsAddFriend === true;
  const hasLegacyMultiShopSelection =
    requiresSingleShop && form.shopIds.length > 1;
  const selectedShopId =
    requiresSingleShop && form.shopIds.length === 1 ? form.shopIds[0] : null;
  const conditionFieldLabel =
    eventConditionKind === "group"
      ? "Chọn nhóm"
      : isZaloTagEvent(form.eventType)
        ? "Chọn tag Zalo"
        : "Chọn tag akaBiz";
  const shopFieldLabel = requiresSingleShop
    ? "Chọn tài khoản Zalo tương ứng"
    : "Áp dụng cho Zalo";
  const shopFieldHint = requiresSingleShop
    ? "Loại sự kiện này chỉ cho phép đúng 1 tài khoản Zalo."
    : null;

  // Reset when bot changes (edit mode)
  useEffect(() => {
    setForm(seed);
    setError(null);
  }, [seed]);

  // Lazy-load groups when needed
  useEffect(() => {
    if (eventConditionKind !== "group") return;
    if (!selectedShopId) return;
    const toFetch = [selectedShopId].filter((id) => !groupsRef.current[id]);
    if (toFetch.length === 0) return;
    toFetch.forEach((id) => {
      groupsRef.current[id] = "loading";
    });
    Promise.allSettled(
      toFetch.map((id) =>
        shopApi.getContacts(id, 0, 1, 500, { isGroup: true }),
      ),
    ).then((results) => {
      const updates = {};
      results.forEach((r, i) => {
        const shopId = toFetch[i];
        let list = [];
        if (r.status === "fulfilled") {
          const payload = r.value;
          const data = Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.data?.contacts)
              ? payload.data.contacts
              : Array.isArray(payload)
                ? payload
                : [];
          list = data.filter((c) => c && c.isGroup);
        }
        updates[shopId] = list;
        groupsRef.current[shopId] = list;
      });
      setGroupsByShop((prev) => ({ ...prev, ...updates }));
    });
  }, [eventConditionKind, selectedShopId]);

  const update = (patch) => {
    setError(null);
    setForm((prev) => {
      const next = { ...prev, ...patch };
      const eventTypeChanged =
        hasOwn(patch, "eventType") && patch.eventType !== prev.eventType;
      const nextEventType = hasOwn(patch, "eventType")
        ? patch.eventType
        : prev.eventType;
      const nextEventMeta = eventTypeMetaByAction.get(nextEventType) || null;
      const nextRequiresSingleShop = nextEventMeta?.conditionKind != null;
      const shopIdsChanged =
        hasOwn(patch, "shopIds") && !areArraysEqual(patch.shopIds, prev.shopIds);

      if (eventTypeChanged || (shopIdsChanged && nextRequiresSingleShop)) {
        next.conditionValue = [];
      }

      if (nextEventMeta?.supportsAddFriend !== true) {
        next.isAddFriend = false;
      }

      return next;
    });
  };

  const handleSubmit = async () => {
    setError(null);
    if (!form.contentReply?.trim()) {
      setError("Nội dung trả lời không được để trống.");
      return;
    }
    if (requiresSingleShop && form.shopIds.length !== 1) {
      setError("Loại sự kiện này yêu cầu chọn đúng 1 tài khoản Zalo.");
      return;
    }
    if (!requiresSingleShop && (!form.shopIds || form.shopIds.length === 0)) {
      setError("Vui lòng chọn ít nhất một tài khoản Zalo.");
      return;
    }
    if (eventConditionKind === "tag" && (!form.conditionValue || form.conditionValue.length === 0)) {
      setError("Vui lòng chọn ít nhất một tag cho sự kiện này.");
      return;
    }
    if (eventConditionKind === "group" && (!form.conditionValue || form.conditionValue.length === 0)) {
      setError("Vui lòng chọn ít nhất một nhóm cho sự kiện này.");
      return;
    }
    if (form.scheduleType === "delay" && !form.delayValue) {
      setError("Vui lòng nhập khoảng thời gian.");
      return;
    }
    if (form.scheduleType === "specific_time" && !form.specificTime) {
      setError("Vui lòng chọn giờ cụ thể.");
      return;
    }

    try {
      setSaving(true);
      await onSubmit({ ...form, chatbotType });
    } catch (err) {
      setError(err?.message || "Có lỗi xảy ra khi lưu.");
    } finally {
      setSaving(false);
    }
  };

  const conditionOptions = useMemo(() => {
    if (!selectedShopId) return [];

    if (isZaloTagEvent(form.eventType)) {
      const seen = new Set();
      return zaloTagsList
        .filter((t) => String(t.shopId || "") === String(selectedShopId))
        .map((t) => String(t.text || "").trim())
        .filter((value) => {
          if (!value || seen.has(value)) return false;
          seen.add(value);
          return true;
        })
        .map((value) => ({
          value,
          label: value,
        }));
    }
    if (isAkabizTagEvent(form.eventType)) {
      const seen = new Set();
      return tagsList
        .filter((t) => String(t.shopId || "") === String(selectedShopId))
        .map((t) => String(t.name || "").trim())
        .filter((value) => {
          if (!value || seen.has(value)) return false;
          seen.add(value);
          return true;
        })
        .map((value) => ({
          value,
          label: value,
        }));
    }
    if (eventConditionKind === "group") {
      const list = groupsByShop[selectedShopId];
      if (!Array.isArray(list)) return [];

      const seen = new Set();
      return list.reduce((out, group) => {
        const key = String(group.uid || group._id || group.id || "").trim();
        if (!key || seen.has(key)) return out;
        seen.add(key);
        out.push({
          value: key,
          label: group.name || key,
          sub: `${group.totalMembers || 0} tv`,
        });
        return out;
      }, []);
    }
    return [];
  }, [
    eventConditionKind,
    form.eventType,
    selectedShopId,
    zaloTagsList,
    tagsList,
    groupsByShop,
  ]);

  const groupOptionsLoading =
    eventConditionKind === "group" &&
    selectedShopId &&
    groupsRef.current[selectedShopId] === "loading";
  const conditionFieldHint = !selectedShopId
    ? hasLegacyMultiShopSelection
      ? "Bot này đang có nhiều Zalo. Hãy giữ lại đúng 1 tài khoản Zalo rồi mới chọn điều kiện kích hoạt."
      : "Hãy chọn đúng 1 tài khoản Zalo trước khi cấu hình điều kiện kích hoạt."
    : null;
  const conditionEmptyLabel = !selectedShopId
    ? "Hãy chọn đúng 1 tài khoản Zalo trước."
    : groupOptionsLoading
      ? "Đang tải danh sách nhóm..."
      : eventConditionKind === "group"
        ? "Không có nhóm nào cho Zalo đã chọn."
        : "Không có tag nào cho Zalo đã chọn.";
  const conditionPlaceholder =
    eventConditionKind === "group"
      ? "Chọn nhóm trigger"
      : isZaloTagEvent(form.eventType)
        ? "Chọn tag Zalo trigger"
        : "Chọn tag trigger";

  const shopSection = (
    <Section icon={Building2} title="Tài khoản Zalo gửi đi">
      <FormRow label={shopFieldLabel} hint={shopFieldHint}>
        <ShopMultiSelect
          shops={shopsList}
          selected={form.shopIds}
          onChange={(values) => update({ shopIds: values })}
          selectionMode={requiresSingleShop ? "single" : "multiple"}
          placeholder={
            requiresSingleShop
              ? "Chọn 1 tài khoản Zalo"
              : "Chọn tài khoản Zalo gửi đi"
          }
        />
      </FormRow>
      {hasLegacyMultiShopSelection && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm px-4 py-3">
          Bot này đang có nhiều tài khoản Zalo cho một sự kiện chỉ hỗ trợ 1
          shop. Hãy giữ lại đúng 1 tài khoản Zalo trước khi lưu.
        </div>
      )}
    </Section>
  );

  const conditionSection =
    eventConditionKind != null ? (
      <Section icon={CheckSquare} title="Điều kiện kích hoạt">
        <FormRow label={conditionFieldLabel} hint={conditionFieldHint}>
          <MultiSelect
            options={conditionOptions}
            selected={form.conditionValue}
            onChange={(values) => update({ conditionValue: values })}
            emptyLabel={conditionEmptyLabel}
            placeholder={conditionPlaceholder}
            disabled={!selectedShopId}
          />
        </FormRow>
      </Section>
    ) : null;

  return (
    <div className="max-w-4xl mx-auto p-6 pb-32 space-y-4">
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3 flex items-start gap-2">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* Section 1: Basic info */}
      <Section icon={Tag} title="Thông tin cơ bản">
        <FormRow label="Mô tả">
          <input
            type="text"
            value={form.description}
            onChange={(e) => update({ description: e.target.value })}
            placeholder="Vd: Chào mừng khách thêm tag VIP"
            className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 outline-none transition"
          />
        </FormRow>
        <FormRow label="Loại sự kiện">
          <select
            value={form.eventType}
            onChange={(e) => update({ eventType: e.target.value })}
            className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 outline-none transition bg-white"
          >
            <option value="">-- Chọn loại sự kiện --</option>
            {eventTypes.map((et) => (
              <option key={et.action} value={et.action}>
                {et.actionName || et.action}
              </option>
            ))}
          </select>
        </FormRow>
      </Section>

      {requiresSingleShop && shopSection}
      {conditionSection}

      <Section icon={Users} title="Đối tượng nhận tin">
        <FormRow label="Gửi cho">
          <div className="flex gap-2 flex-wrap">
            {TARGET_OPTIONS.map((opt) => (
              <RadioCard
                key={opt.value}
                label={opt.label}
                checked={form.targetType === opt.value}
                onClick={() => update({ targetType: opt.value })}
              />
            ))}
          </div>
        </FormRow>
      </Section>

      {/* Section 3: Schedule */}
      <Section icon={CalendarClock} title="Lịch trình gửi">
        <FormRow label="Thời điểm gửi">
          <div className="flex gap-2 flex-wrap">
            {SCHEDULE_OPTIONS.map((opt) => (
              <RadioCard
                key={opt.value}
                label={opt.label}
                checked={form.scheduleType === opt.value}
                onClick={() => update({ scheduleType: opt.value })}
              />
            ))}
          </div>
        </FormRow>
        {form.scheduleType === "delay" && (
          <FormRow label="Khoảng thời gian">
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                value={form.delayValue ?? ""}
                onChange={(e) =>
                  update({ delayValue: e.target.value ? parseInt(e.target.value, 10) : null })
                }
                placeholder="Số lượng"
                className="w-32 px-3 py-2 text-sm rounded-md border border-gray-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 outline-none"
              />
              <select
                value={form.delayUnit || "minutes"}
                onChange={(e) => update({ delayUnit: e.target.value })}
                className="px-3 py-2 text-sm rounded-md border border-gray-200 bg-white"
              >
                {DELAY_UNITS.map((u) => (
                  <option key={u.value} value={u.value}>
                    {u.label}
                  </option>
                ))}
              </select>
            </div>
          </FormRow>
        )}
        {form.scheduleType === "specific_time" && (
          <FormRow label="Giờ cụ thể">
            <input
              type="time"
              value={form.specificTime || ""}
              onChange={(e) => update({ specificTime: e.target.value })}
              className="px-3 py-2 text-sm rounded-md border border-gray-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 outline-none"
            />
          </FormRow>
        )}
      </Section>

      {/* Section 4: Content */}
      <Section icon={Send} title="Nội dung gửi tin nhắn">
        <FormRow label="Nội dung" hint="Tin nhắn sẽ được gửi cho khách khi sự kiện kích hoạt.">
          <textarea
            value={form.contentReply}
            onChange={(e) => update({ contentReply: e.target.value })}
            rows={5}
            placeholder="Nhập nội dung tin nhắn..."
            className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 outline-none transition resize-y"
          />
        </FormRow>
      </Section>

      {!requiresSingleShop && shopSection}

      {/* Section 6: Advanced */}
      {supportsAddFriend && (
        <Section icon={Sliders} title="Tùy chọn nâng cao">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isAddFriend}
              onChange={(e) => update({ isAddFriend: e.target.checked })}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <div>
              <div className="text-sm font-medium text-gray-900">
                Tự động kết bạn với thành viên nhóm
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                Khi gửi tin cho user vừa join/leave nhóm, bot sẽ gửi lời mời kết
                bạn nếu chưa là bạn bè.
              </div>
            </div>
          </label>
        </Section>
      )}

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-[380px] right-0 bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-end gap-2 shadow-lg z-20">
        {mode === "create" && onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-md border border-gray-200 hover:bg-gray-50 inline-flex items-center gap-1.5"
          >
            <X size={14} />
            Hủy
          </button>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50 inline-flex items-center gap-1.5 shadow-sm"
        >
          <Save size={14} />
          {saving
            ? "Đang lưu..."
            : mode === "create"
              ? "Tạo bot"
              : "Lưu thay đổi"}
        </button>
      </div>
    </div>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <section className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
    <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
      <span className="w-7 h-7 rounded-md bg-indigo-50 text-indigo-600 inline-flex items-center justify-center">
        <Icon size={14} />
      </span>
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    </div>
    <div className="p-5 space-y-3">{children}</div>
  </section>
);

const FormRow = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1.5">
      {label}
    </label>
    {children}
    {hint && <div className="text-[11px] text-gray-500 mt-1">{hint}</div>}
  </div>
);

const RadioCard = ({ label, checked, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "px-3 py-1.5 text-sm rounded-md border transition-colors",
      checked
        ? "bg-indigo-50 border-indigo-300 text-indigo-700 font-medium ring-1 ring-indigo-200"
        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50",
    )}
  >
    {label}
  </button>
);

const MultiSelect = ({
  options,
  selected = [],
  onChange,
  emptyLabel,
  placeholder,
  selectionMode = "multiple",
  disabled = false,
}) => {
  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => String(o.label || "").toLowerCase().includes(q));
  }, [options, search]);
  const set = useMemo(() => new Set(selected.map(String)), [selected]);
  const toggle = (val) => {
    const key = String(val);
    if (selectionMode === "single") {
      if (set.size === 1 && set.has(key)) {
        onChange([]);
        return;
      }
      onChange([key]);
      return;
    }

    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(Array.from(next));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "w-full text-left px-3 py-2 text-sm rounded-md border border-gray-200 bg-white inline-flex items-center justify-between gap-2 min-h-[40px]",
            disabled
              ? "cursor-not-allowed bg-gray-50 text-gray-400"
              : "hover:bg-gray-50",
          )}
        >
          <span className="flex flex-wrap gap-1 items-center min-w-0 flex-1">
            {selected.length === 0 ? (
              <span className="text-gray-400">{placeholder}</span>
            ) : (
              <>
                {selected.slice(0, 4).map((v) => (
                  <span
                    key={v}
                    className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700"
                  >
                    {options.find((o) => String(o.value) === String(v))?.label || v}
                  </span>
                ))}
                {selected.length > 4 && (
                  <span className="text-[11px] text-gray-500">
                    +{selected.length - 4}
                  </span>
                )}
              </>
            )}
          </span>
          <Plus size={14} className="text-gray-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="p-2 border-b border-gray-100">
          <div className="relative">
            <Search
              size={12}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm..."
              className="w-full pl-7 pr-2 py-1.5 text-sm rounded border border-gray-200 outline-none focus:border-indigo-400"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <div className="text-xs text-gray-500 italic px-2 py-3 text-center">
              {emptyLabel}
            </div>
          ) : (
            filtered.map((opt) => {
              const checked = set.has(String(opt.value));
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggle(opt.value)}
                  className={cn(
                    "w-full text-left px-2 py-1.5 text-sm rounded-md inline-flex items-center justify-between gap-2 transition-colors",
                    checked ? "bg-indigo-50 text-indigo-700" : "hover:bg-gray-50",
                  )}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {checked ? (
                      <CheckSquare size={14} className="text-indigo-600 shrink-0" />
                    ) : (
                      <Square size={14} className="text-gray-300 shrink-0" />
                    )}
                    <span className="truncate">{opt.label}</span>
                  </span>
                  {opt.sub && (
                    <span className="text-[10px] text-gray-500 shrink-0">
                      {opt.sub}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const ShopMultiSelect = ({
  shops,
  selected,
  onChange,
  selectionMode = "multiple",
  placeholder = "Chọn tài khoản Zalo gửi đi",
}) => {
  const options = useMemo(
    () =>
      shops.map((s) => ({
        value: s.id,
        label: s.name || s.zaloName || s.id,
        sub: s.isLogin ? "Đã đăng nhập" : "Chưa đăng nhập",
      })),
    [shops],
  );
  return (
    <MultiSelect
      options={options}
      selected={selected}
      onChange={onChange}
      selectionMode={selectionMode}
      placeholder={placeholder}
      emptyLabel="Không có Zalo nào"
    />
  );
};

export default BotConfigForm;
